import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { CheckinCard, listCheckinCards } from '../../../../api/stays-checkin-client';
import type { StaysListingHead } from '../../../../interfaces/stays';
import { ClockNote } from '../../../clock-note/clock-note';
import { useNotificationFrames } from '../../../notifications/use-notification-frames';
import { Staleable, StateSwitch } from '../../../../motion';
import { ListSkeleton, LoadError, skeletonHeight } from '../../dashboard/dashboard-shared-components/load-state';
import { Empty, EmptyText } from '../../dashboard/dashboard-stays-components/dashboard-stays.styled';
import { buildingClock } from '../stays-reservations-model';
import { palette, spacing, typography } from '../../../../style-utils/theme';
import { CheckinCardView } from './checkin-card';
import { CheckinSettingsForm } from './checkin-settings';
import { HouseRulesEditor } from './house-rules-editor';

/**
 * A LISTING'S CHECK-IN TAB (plan S6; appended to S4's STAYS_LISTING_TABS):
 *   1. the guest cards of the stays still to come (one per stay: party, ID
 *      checks, signed rules, payment, the link and its actions), live — a
 *      notification frame about a stay on screen refetches, and the page
 *      refreshes every 30 s while it is visible;
 *   2. the house rules editor;
 *   3. the check-in settings.
 */

const REFRESH_MS = 30_000;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
  min-width: 0;
`;

const Cards = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  min-width: 0;
`;

const H = styled.h3`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

export const CheckinTab: React.FC<{ listing: StaysListingHead; canManage: boolean }> = ({ listing, canManage }) => {
  const [view, setView] = useState<'loading' | 'error' | 'empty' | 'ready'>('loading');
  const [cards, setCards] = useState<CheckinCard[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const seq = useRef(0);
  const shown = useRef(new Set<string>());

  const load = useCallback(
    async (quiet = false) => {
      const mine = ++seq.current;
      if (quiet) setRefreshing(true);
      else setView('loading');
      try {
        const out = await listCheckinCards(listing.id);
        if (mine !== seq.current) return;
        setCards(out.cards);
        shown.current = new Set(out.cards.map(c => c.reservation_id));
        setView(out.cards.length ? 'ready' : 'empty');
      } catch {
        if (mine !== seq.current) return;
        if (!quiet) setView('error');
      } finally {
        if (mine === seq.current) setRefreshing(false);
      }
    },
    [listing.id]
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  // The fallback to the socket: every 30 s while the page is visible.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load(true);
    }, REFRESH_MS);

    return () => window.clearInterval(id);
  }, [load]);

  // Live: a frame about a stay on screen.
  useNotificationFrames(row => {
    if (row.subject_type === 'stays_reservation' && row.subject_id && shown.current.has(row.subject_id)) void load(true);
  });

  const onChanged = (id: string) => (next: CheckinCard | null) => {
    if (next) setCards(list => list.map(c => (c.reservation_id === id ? next : c)));
    else void load(true);
  };

  return (
    <Page>
      <Cards aria-labelledby="checkin-cards-title" data-testid="checkin-cards">
        <H id="checkin-cards-title">Guest check-ins</H>
        <ClockNote clock={buildingClock(listing.timezone)} />
        <StateSwitch state={view} minHeight={skeletonHeight(2, 160)}>
          {view === 'loading' ? <ListSkeleton rows={2} rowHeight={160} label="check-ins" /> : null}
          {view === 'error' ? <LoadError message="Couldn't load the check-ins" onRetry={() => void load(false)} /> : null}
          {view === 'empty' ? (
            <Empty role="status">
              <EmptyText>No stays to come. A stay you add shows its check-in here.</EmptyText>
            </Empty>
          ) : null}
          {view === 'ready' ? (
            <Staleable stale={refreshing}>
              <Cards>
                {cards.map(c => (
                  <CheckinCardView key={c.reservation_id} card={c} canManage={canManage} onChanged={onChanged(c.reservation_id)} />
                ))}
              </Cards>
            </Staleable>
          ) : null}
        </StateSwitch>
      </Cards>
      <HouseRulesEditor listingId={listing.id} canManage={canManage} />
      <CheckinSettingsForm listingId={listing.id} canManage={canManage} />
    </Page>
  );
};
